import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    addUser,
    addUsers,
    findUserNamesInUse,
    generateNumberedUserNames,
    generateRandomPin,
    isUsernameInUse,
    MAX_BULK_PLAYER_CREATE,
    normalizeUserName,
    type BulkPlayerDraft,
} from "../../services/admin/UserService.tsx";
import { Users } from "../../models/User.tsx";
import Switch from 'react-switch';
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import "../../styles/admin/UserActivity.css"
import "../../styles/admin/Menu.css"
import { logUserActivity } from "../../services/player/LoginService.tsx";
import { useUser } from "../../context/UserContext.tsx";
import AgentContext from "../../context/admin/Agent.tsx";
import { filterMessagingUsers } from "../../helpers/messagingRecipients.ts";

type CreateMode = 'single' | 'bulk';
type BulkSource = 'generate' | 'list';
type BulkRowStatus = 'ok' | 'taken' | 'duplicate';

type ParsedPlayerRow = {
    userName: string;
    pin?: string;
    duplicateInList: boolean;
};

type BulkReviewRow = ParsedPlayerRow & {
    status: BulkRowStatus;
};

const LIST_HEADERS = new Set(['USERNAME', 'USER', 'PLAYER', 'NAME', 'PASSWORD', 'PIN']);

const parsePlayerRows = (text: string): ParsedPlayerRow[] => {
    const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const seen = new Set<string>();
    const players: ParsedPlayerRow[] = [];

    lines.forEach(line => {
        const [rawName, rawPin] = line.split(/[,;\t]/).map(part => part?.trim());
        const userName = normalizeUserName(rawName || '');
        if (!userName || LIST_HEADERS.has(userName)) return;

        players.push({
            userName,
            pin: rawPin ? rawPin.toUpperCase() : undefined,
            duplicateInList: seen.has(userName),
        });
        seen.add(userName);
    });

    return players;
};

const downloadExcelTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
        ['Username', 'Password'],
        ['PLAYER01', ''],
        ['PLAYER02', 'AB12C'],
    ]);
    worksheet['!cols'] = [{ wch: 18 }, { wch: 16 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Players');
    XLSX.writeFile(workbook, 'PlayerImportTemplate.xlsx');
};

const RegisterPlayer = () => {

    const [searchResults, setSearchResults] = useState<Users[]>([]);
    const { user: currentUser } = useUser();
    const { allAgents } = useContext(AgentContext);
    const [randomPin, setRandomPin] = useState(true);
    const [parentInput, setParentInput] = useState('');
    const [createMode, setCreateMode] = useState<CreateMode>('single');
    const [bulkSource, setBulkSource] = useState<BulkSource>('generate');
    const [bulkPrefix, setBulkPrefix] = useState('');
    const [bulkStart, setBulkStart] = useState(1);
    const [bulkCount, setBulkCount] = useState(10);
    const [bulkPad, setBulkPad] = useState(1);
    const [bulkListText, setBulkListText] = useState('');
    const [bulkSharedPin, setBulkSharedPin] = useState('');
    const [formData, setFormData] = useState<{
        master: string; parent: string; userName: string; pin: string; selectedAgent: string | null;
    }>({
        master: '', parent: '', userName: '', pin: '', selectedAgent: null
    });
    const [createdUserName, setCreatedUserName] = useState<string | null>(null);
    const [createdPlayers, setCreatedPlayers] = useState<Array<{ userName: string; pin: string }>>([]);
    const [skippedPlayers, setSkippedPlayers] = useState<Array<{ userName: string; reason: string }>>([]);
    const [takenNames, setTakenNames] = useState<Set<string>>(new Set());
    const [isCheckingNames, setIsCheckingNames] = useState(false);
    const [awaitingConflictChoice, setAwaitingConflictChoice] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [usernameTaken, setUsernameTaken] = useState(false);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const modalEl = modalRef.current;
        if (!modalEl) return;

        const handleHidden = () => {
            setIsLoading(false);
            setIsSuccess(false);
            setErrorMessage(null);
            setAwaitingConflictChoice(false);
        };

        modalEl.addEventListener('hidden.bs.modal', handleHidden);

        return () => {
            modalEl.removeEventListener('hidden.bs.modal', handleHidden);
        };
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        if (name === 'parent') {
            setParentInput(value);

            if (value.trim() !== '') {
                setSearchResults(filterMessagingUsers(allAgents, value.trim()));
            } else {
                setSearchResults([]);
            }

            return;
        }

        if (name === 'userName') {
            setUsernameTaken(false);
            setFormData(prev => ({
                ...prev,
                userName: value.toUpperCase(),
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUsernameBlur = async () => {
        const userName = normalizeUserName(formData.userName);
        if (!userName) {
            setUsernameTaken(false);
            return;
        }

        setUsernameTaken(await isUsernameInUse(userName));
    };

    const applySelectedAgent = (selectedAgent: Users) => {
        setFormData(prev => ({
            ...prev,
            parent: selectedAgent.userName,
            master: selectedAgent.parent ?? '',
            selectedAgent: selectedAgent.userName,
        }));
        setParentInput(selectedAgent.userName);
        setSearchResults([]);
    };

    const handleResultSelect = (selectedUserName: string) => {
        const selectedAgent = searchResults.find(
            u => u.userName === selectedUserName
        );

        if (!selectedAgent) return;
        applySelectedAgent(selectedAgent);
    };

    const setHierarchyForSubAgent = () => {
        if (currentUser!.userType !== 'subAgent') return;

        setFormData(prevFormData => ({
            ...prevFormData,
            parent: currentUser!.userName,
            master: currentUser!.parent ?? '',
        }));
    };

    const resetCreateForm = () => {
        const isSubAgent = currentUser?.userType === 'subAgent';

        setFormData({
            pin: randomPin ? generateRandomPin() : '',
            userName: '',
            selectedAgent: null,
            parent: isSubAgent ? currentUser.userName : '',
            master: isSubAgent ? (currentUser.parent ?? '') : '',
        });
        setParentInput('');
        setSearchResults([]);
        setUsernameTaken(false);
        setCreatedPlayers([]);
        setSkippedPlayers([]);
        setTakenNames(new Set());
        setAwaitingConflictChoice(false);
        setBulkListText('');
        setBulkSharedPin('');
    };

    useEffect(() => {
        setHierarchyForSubAgent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const parentUser = currentUser?.userType === "masterAgent"
        ? formData.selectedAgent ?? undefined
        : currentUser?.userName;

    const masterUser = currentUser?.userType === "masterAgent"
        ? formData.master || currentUser.userName
        : currentUser?.parent ?? '';

    const generatedNames = useMemo(() => {
        const count = Math.min(Math.max(0, Number(bulkCount) || 0), MAX_BULK_PLAYER_CREATE);
        if (count === 0) return [];
        return generateNumberedUserNames(bulkPrefix, Number(bulkStart) || 1, count, bulkPad);
    }, [bulkPrefix, bulkStart, bulkCount, bulkPad]);

    const parsedRows = useMemo(() => parsePlayerRows(bulkListText), [bulkListText]);

    const sourceRows: ParsedPlayerRow[] = useMemo(() => {
        if (bulkSource === 'generate') {
            return generatedNames.map(userName => ({
                userName,
                duplicateInList: false,
            }));
        }
        return parsedRows;
    }, [bulkSource, generatedNames, parsedRows]);

    const reviewRows: BulkReviewRow[] = useMemo(() => (
        sourceRows.map(row => ({
            ...row,
            status: row.duplicateInList
                ? 'duplicate'
                : takenNames.has(row.userName)
                    ? 'taken'
                    : 'ok',
        }))
    ), [sourceRows, takenNames]);

    const uniqueCandidates = useMemo(
        () => sourceRows.filter(row => !row.duplicateInList).slice(0, MAX_BULK_PLAYER_CREATE),
        [sourceRows]
    );

    const bulkDrafts: BulkPlayerDraft[] = useMemo(() => (
        uniqueCandidates.map(player => ({
            userName: player.userName,
            pin: player.pin || '',
            parent: parentUser,
            master: masterUser,
        }))
    ), [uniqueCandidates, parentUser, masterUser]);

    const validDrafts = useMemo(
        () => bulkDrafts.filter(player => !takenNames.has(player.userName)),
        [bulkDrafts, takenNames]
    );

    const displayRows = useMemo(() => (
        [...reviewRows].sort((left, right) => {
            if (left.status === 'ok' && right.status !== 'ok') return 1;
            if (left.status !== 'ok' && right.status === 'ok') return -1;
            return 0;
        })
    ), [reviewRows]);
    const conflictRows = reviewRows.filter(row => row.status !== 'ok');
    const takenUserNames = [...new Set(reviewRows.filter(row => row.status === 'taken').map(row => row.userName))];
    const duplicateUserNames = [...new Set(reviewRows.filter(row => row.status === 'duplicate').map(row => row.userName))];

    const candidateNamesKey = uniqueCandidates.map(row => row.userName).join('\n');

    useEffect(() => {
        if (createMode !== 'bulk') {
            setTakenNames(new Set());
            setIsCheckingNames(false);
            return;
        }

        if (!candidateNamesKey) {
            setTakenNames(new Set());
            setIsCheckingNames(false);
            return;
        }

        const names = candidateNamesKey.split('\n').filter(Boolean);
        let cancelled = false;
        setIsCheckingNames(true);
        setAwaitingConflictChoice(false);

        const timer = window.setTimeout(async () => {
            try {
                const used = await findUserNamesInUse(names);
                if (!cancelled) setTakenNames(used);
            } catch {
                if (!cancelled) setTakenNames(new Set());
            } finally {
                if (!cancelled) setIsCheckingNames(false);
            }
        }, 400);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [createMode, candidateNamesKey]);

    const submitBulkPlayers = async (createValidOnly: boolean) => {
        if (currentUser?.userType === 'masterAgent' && !formData.selectedAgent) {
            setErrorMessage('Please select an agent.');
            return;
        }
        if (bulkSource === 'generate' && !normalizeUserName(bulkPrefix)) {
            setErrorMessage('Enter a prefix for the player usernames.');
            return;
        }
        if (bulkDrafts.length === 0) {
            setErrorMessage(bulkSource === 'generate'
                ? 'Enter a prefix and how many players to create.'
                : 'Paste or upload at least one username.');
            return;
        }

        setIsLoading(true);
        setIsSuccess(false);
        setErrorMessage(null);

        try {
            const used = await findUserNamesInUse(bulkDrafts.map(player => player.userName));
            setTakenNames(used);
            setIsCheckingNames(false);

            const availableDrafts = bulkDrafts.filter(player => !used.has(player.userName));
            const blockedNames = bulkDrafts
                .filter(player => used.has(player.userName))
                .map(player => player.userName);

            if (!createValidOnly && blockedNames.length > 0) {
                setAwaitingConflictChoice(true);
                setIsLoading(false);
                return;
            }

            const draftsToCreate = availableDrafts.map(player => ({
                ...player,
                pin: player.pin || (randomPin ? generateRandomPin() : bulkSharedPin.toUpperCase()),
            }));

            if (draftsToCreate.length === 0) {
                setAwaitingConflictChoice(true);
                setErrorMessage('None of these usernames can be created. Change the names marked in red.');
                setIsLoading(false);
                return;
            }

            if (draftsToCreate.some(player => !player.pin)) {
                setErrorMessage('Enter a password for these players, or turn on random passwords.');
                setIsLoading(false);
                return;
            }

            setAwaitingConflictChoice(false);
            const result = await addUsers(draftsToCreate);
            setCreatedPlayers(result.created);
            setSkippedPlayers(result.skipped);
            setCreatedUserName(result.created[0]?.userName ?? null);

            if (result.error) {
                setErrorMessage(result.error);
                if (result.created.length > 0) setIsSuccess(true);
                return;
            }

            if (result.created.length === 0) {
                setErrorMessage(result.skipped.length > 0
                    ? 'No players were created. Usernames are already in use or invalid.'
                    : 'No players were created.');
                return;
            }

            setIsSuccess(true);
        } catch (error: any) {
            setErrorMessage(error?.message || 'Error creating players. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (createMode === 'bulk') {
            await submitBulkPlayers(false);
            return;
        }

        if (currentUser?.userType === 'masterAgent' && !formData.selectedAgent) {
            setErrorMessage('Please select an agent.');
            return;
        }

        const upperUserName = normalizeUserName(formData.userName);

        if (!upperUserName) {
            setErrorMessage('Please enter a player username.');
            return;
        }

        setIsLoading(true);
        setIsSuccess(false);
        setErrorMessage(null);

        if (await isUsernameInUse(upperUserName)) {
            setUsernameTaken(true);
            setErrorMessage('Username is already in use');
            setIsLoading(false);
            return;
        }

        const upperPin = formData.pin.toUpperCase();
        setCreatedUserName(upperUserName);
        setCreatedPlayers([{ userName: upperUserName, pin: upperPin }]);
        setSkippedPlayers([]);

        const newUser = {
            userName: upperUserName,
            pin: upperPin,
            parent: parentUser,
            master: masterUser,
            userType: 'Player',
        };

        try {
            await addUser(newUser);

            setIsSuccess(true);
            setUsernameTaken(false);
            resetCreateForm();
        } catch (error: any) {
            setErrorMessage(
                error?.message || 'Error creating player. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (randomPin) {
            setFormData(prev => ({
                ...prev,
                pin: generateRandomPin()
            }));
        }
    }, [randomPin]);

    const navigate = useNavigate();

    const handleAddAnother = () => {
        resetCreateForm();
        setIsSuccess(false);
        setErrorMessage(null);
        setIsLoading(false);
    };

    const handleDone = () => {
        if (createdPlayers.length > 1) {
            logUserActivity(currentUser!.userName, "View Customer Admin");
            navigate('/customerAdmin');
            return;
        }

        logUserActivity(currentUser!.userName, `View User Basic: ${createdUserName}`);
        navigate(`/user/${createdUserName}`, {
            state: { from: "PlayerList" }
        });
    };

    const handleListFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            if (/\.(xlsx|xls|csv)$/i.test(file.name)) {
                const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, { header: 1 });
                const text = rows
                    .map(row => [row?.[0], row?.[1]].filter(value => value !== undefined && value !== '').join(','))
                    .join('\n');
                setBulkListText(text);
                setBulkSource('list');
            } else {
                setBulkListText(await file.text());
                setBulkSource('list');
            }
        } catch {
            setErrorMessage('Could not read that file. Use a text, CSV, or Excel list.');
        } finally {
            event.target.value = '';
        }
    };

    const createButtonCount = createMode === 'bulk'
        ? (takenNames.size > 0 || duplicateUserNames.length > 0 ? validDrafts.length : bulkDrafts.length)
        : 0;
    const showConflictChoice = createMode === 'bulk'
        && !isSuccess
        && !isLoading
        && (awaitingConflictChoice || conflictRows.length > 0);

    return (
        <div>
            <div className="mt-2 container-fluid">
                <div className="col container-title-addCustomer align-items-center">
                    <span>Add Player</span>
                    <button className="button-agent" data-bs-toggle="modal" data-bs-target="#addCustomerModal">
                        {createMode === 'bulk' && createButtonCount > 1 ? `Create ${createButtonCount}` : 'Create'}
                    </button>
                </div>
                <div className="opcion-add">
                    <div className="add-player-mode">
                        <button
                            type="button"
                            className={`add-player-mode-btn${createMode === 'single' ? ' active' : ''}`}
                            onClick={() => setCreateMode('single')}
                        >
                            One player
                        </button>
                        <button
                            type="button"
                            className={`add-player-mode-btn${createMode === 'bulk' ? ' active' : ''}`}
                            onClick={() => setCreateMode('bulk')}
                        >
                            Many players
                        </button>
                    </div>
                    <div className="row">
                        {currentUser!.userType === 'masterAgent' && (
                            <div className="col-lg-3 col-md-3">
                                <span>Under which SubAgent?</span>
                                <input
                                    type="search"
                                    className="form-control input-agent"
                                    name="parent"
                                    value={parentInput}
                                    onChange={handleInputChange}
                                    autoComplete="off"
                                />
                                {searchResults.length > 0 && (
                                    <div className="search-container">
                                        {searchResults.map((user) => (
                                            <div key={user.id ?? user.userName} className="search-result" onClick={() => handleResultSelect(user.userName)} >
                                                {user.userName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {createMode === 'single' && (
                            <div className="col-lg-3 col-md-4 col-8 mb-3">
                                <span>Username</span>
                                <input
                                    type="text"
                                    className="form-control input-agent"
                                    name="userName"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    onBlur={handleUsernameBlur}
                                    autoComplete="off"
                                    placeholder="Enter a unique name"
                                    required
                                />
                                {usernameTaken && (
                                    <small className="text-danger">This username is already in use.</small>
                                )}
                            </div>
                        )}
                        <div className="col-lg-4 col-md-4 mb-3 random-password-field">
                            <span className="random-password-label">Password</span>
                            <div className="center-switch">
                                <Switch id='active' name="pin" checked={randomPin}
                                    onChange={() => {
                                        const newValue = !randomPin;
                                        setRandomPin(newValue);
                                        setFormData(prev => ({
                                            ...prev,
                                            pin: newValue ? generateRandomPin() : ''
                                        }));
                                    }}
                                    offColor="#ff6347"
                                    onColor="#4caf50"
                                    offHandleColor="#ffffff"
                                    onHandleColor="#ffffff"
                                />
                                <span>Generate random password</span>
                            </div>
                        </div>
                    </div>

                    {createMode === 'bulk' && (
                        <div className="bulk-player-panel">
                            <div className="add-player-mode">
                                <button
                                    type="button"
                                    className={`add-player-mode-btn${bulkSource === 'generate' ? ' active' : ''}`}
                                    onClick={() => setBulkSource('generate')}
                                >
                                    Numbered names
                                </button>
                                <button
                                    type="button"
                                    className={`add-player-mode-btn${bulkSource === 'list' ? ' active' : ''}`}
                                    onClick={() => setBulkSource('list')}
                                >
                                    Paste / upload list
                                </button>
                            </div>

                            {bulkSource === 'generate' ? (
                                <div className="row">
                                    <div className="col-lg-3 col-md-4 mb-3">
                                        <span>Prefix</span>
                                        <input
                                            type="text"
                                            className="form-control input-agent"
                                            value={bulkPrefix}
                                            onChange={event => setBulkPrefix(event.target.value.toUpperCase())}
                                            placeholder="JOCK"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="col-lg-2 col-md-4 mb-3">
                                        <span>Start number</span>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form-control input-agent"
                                            value={bulkStart}
                                            onChange={event => setBulkStart(Number(event.target.value))}
                                        />
                                    </div>
                                    <div className="col-lg-2 col-md-4 mb-3">
                                        <span>How many</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={MAX_BULK_PLAYER_CREATE}
                                            className="form-control input-agent"
                                            value={bulkCount}
                                            onChange={event => setBulkCount(Number(event.target.value))}
                                        />
                                    </div>
                                    <div className="col-lg-2 col-md-4 mb-3">
                                        <span>Digits</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={8}
                                            className="form-control input-agent"
                                            value={bulkPad}
                                            onChange={event => {
                                                const digits = Math.floor(Number(event.target.value));
                                                setBulkPad(Number.isFinite(digits) ? Math.min(8, Math.max(1, digits)) : 1);
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <div className="bulk-excel-help">
                                        <strong>Excel / CSV format</strong>
                                        <p>
                                            Column A = <code>Username</code> (required). Column B = <code>Password</code> (optional).
                                            First row can be headers. One player per row, max {MAX_BULK_PLAYER_CREATE}.
                                        </p>
                                        <ul>
                                            <li>If password is empty, a random password is used when that option is on.</li>
                                            <li>You can also paste the same format here, one line per player: <code>PLAYER02,AB12C</code></li>
                                        </ul>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={downloadExcelTemplate}>
                                            Download Excel template
                                        </button>
                                    </div>
                                    <span>Usernames (one per line, optional USERNAME,PASSWORD)</span>
                                    <textarea
                                        className="form-control bulk-list-textarea"
                                        value={bulkListText}
                                        onChange={event => setBulkListText(event.target.value)}
                                        placeholder={"PLAYER01\nPLAYER02,AB12C"}
                                    />
                                    <div className="bulk-file-row">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Upload CSV / Excel
                                        </button>
                                        <small>Up to {MAX_BULK_PLAYER_CREATE} players per batch.</small>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".txt,.csv,.xlsx,.xls"
                                            hidden
                                            onChange={handleListFile}
                                        />
                                    </div>
                                </div>
                            )}

                            {!randomPin && (
                                <div className="col-lg-3 col-md-4 mb-3 px-0">
                                    <span>Password for all players</span>
                                    <input
                                        type="text"
                                        className="form-control input-agent"
                                        value={bulkSharedPin}
                                        onChange={event => setBulkSharedPin(event.target.value.toUpperCase())}
                                        autoComplete="off"
                                    />
                                </div>
                            )}

                            <div className="bulk-preview-box">
                                <div>
                                    <strong>{validDrafts.length}</strong> player{validDrafts.length === 1 ? '' : 's'} ready
                                    {takenUserNames.length > 0 && (
                                        <span className="bulk-taken-count"> · {takenUserNames.length} already in use</span>
                                    )}
                                    {duplicateUserNames.length > 0 && (
                                        <span className="bulk-taken-count"> · {duplicateUserNames.length} duplicated in this list</span>
                                    )}
                                    {isCheckingNames && <span className="bulk-checking"> · checking usernames…</span>}
                                </div>
                                {reviewRows.length > 0 && (
                                    <div className="bulk-review-list">
                                        {displayRows.map((row, index) => (
                                            <div
                                                key={`${row.userName}-${index}`}
                                                className={`bulk-review-row${row.status !== 'ok' ? ' is-blocked' : ''}`}
                                            >
                                                <span className="bulk-review-name">{row.userName}</span>
                                                {row.status === 'taken' && <span>already in use</span>}
                                                {row.status === 'duplicate' && <span>duplicate in this list</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="modal fade" id="addCustomerModal" tabIndex={-1} ref={modalRef}>
                <div className="modal-dialog ">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isLoading
                                    ? (createMode === 'bulk' ? 'Creating Players...' : 'Creating Player...')
                                    : isSuccess
                                        ? 'Success'
                                        : showConflictChoice
                                            ? 'Usernames already in use'
                                            : createMode === 'bulk'
                                                ? `Create ${validDrafts.length || bulkDrafts.length || 0} players?`
                                                : 'Are you sure to add a Player?'}
                            </h5>

                            {!isLoading && (
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                />
                            )}
                        </div>

                        <div className="modal-body text-center">

                            {errorMessage && !isLoading && (
                                <div className="alert alert-danger text-start">
                                    <strong>Error:</strong> {errorMessage}
                                </div>
                            )}

                            {isLoading && (
                                <>
                                    <div className="spinner-border text-primary mb-3" />
                                    <p>
                                        {createMode === 'bulk'
                                            ? `Creating ${validDrafts.length || bulkDrafts.length} players, please wait...`
                                            : 'Creating player, please wait...'}
                                    </p>
                                </>
                            )}

                            {isSuccess && !isLoading && (
                                <>
                                    <div className="text-success fs-1 mb-3">✔</div>
                                    <p>
                                        {createMode === 'bulk'
                                            ? `${createdPlayers.length} player${createdPlayers.length === 1 ? '' : 's'} created.`
                                            : 'Player was created successfully.'}
                                    </p>
                                    {skippedPlayers.length > 0 && (
                                        <div className="alert alert-warning text-start mb-0">
                                            <strong>{skippedPlayers.length} skipped:</strong>
                                            <div className="bulk-skipped-list">
                                                {skippedPlayers.slice(0, 8).map(item => (
                                                    <div key={item.userName}>{item.userName}: {item.reason}</div>
                                                ))}
                                                {skippedPlayers.length > 8 && <div>… +{skippedPlayers.length - 8} more</div>}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {!isLoading && !isSuccess && showConflictChoice && (
                                <div className="text-start">
                                    <p>
                                        These usernames are already in use or duplicated. They are marked with a red line.
                                        You can close this window to change them, or create only the names that are available.
                                    </p>
                                    <div className="bulk-review-list bulk-review-list-modal">
                                        {conflictRows.map((row, index) => (
                                            <div key={`${row.userName}-${index}`} className="bulk-review-row is-blocked">
                                                <span className="bulk-review-name">{row.userName}</span>
                                                <span>{row.status === 'taken' ? 'already in use' : 'duplicate in this list'}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {validDrafts.length > 0 && (
                                        <p className="mb-0 mt-3">
                                            <strong>{validDrafts.length}</strong> player{validDrafts.length === 1 ? '' : 's'} can still be created.
                                        </p>
                                    )}
                                </div>
                            )}

                            {!isLoading && !isSuccess && !errorMessage && !showConflictChoice && (
                                createMode === 'bulk' ? (
                                    <p>
                                        You are creating <strong>{validDrafts.length || bulkDrafts.length}</strong> players
                                        {parentUser ? <> under <strong>{parentUser}</strong></> : ''}.
                                    </p>
                                ) : (
                                    <p>
                                        You are creating player{' '}
                                        <strong>{normalizeUserName(formData.userName) || '—'}</strong>
                                        {' '}under the selected agent.
                                    </p>
                                )
                            )}
                        </div>

                        <div className="button-container-register-player ">

                            {!isLoading && !isSuccess && showConflictChoice && (
                                <>
                                    <button className="btn btn-danger" data-bs-dismiss="modal">
                                        Close to edit
                                    </button>
                                    {validDrafts.length > 0 && (
                                        <button className="btn btn-primary" onClick={() => submitBulkPlayers(true)}>
                                            Create {validDrafts.length} valid player{validDrafts.length === 1 ? '' : 's'}
                                        </button>
                                    )}
                                </>
                            )}

                            {!isLoading && !isSuccess && !showConflictChoice && (
                                <>
                                    <button className="btn btn-danger" data-bs-dismiss="modal">
                                        Close
                                    </button>
                                    <button className="btn btn-primary" onClick={handleSubmit}>
                                        Confirm
                                    </button>
                                </>
                            )}

                            {isSuccess && (
                                <>
                                    <button className="btn btn-secondary" data-bs-dismiss="modal" onClick={handleAddAnother}>
                                        Add Another
                                    </button>
                                    {(createdUserName || createdPlayers.length > 1) && (
                                        <button className="btn btn-success" onClick={handleDone} data-bs-dismiss="modal">
                                            {createdPlayers.length > 1 ? 'Go to Customer Admin' : 'Go to Player'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}
export default RegisterPlayer;
